import axios from 'axios';
import get from 'lodash/get';
import set from 'lodash/set';
import omit from 'lodash/omit';
import qs from 'qs';
import { getUrl } from '@/config/api.routes';
import { composeMessage } from '@/libs';
import { buildCustomerSelection, bulkActionsEnabled } from './selection';


function getCustomersMessagePayload(customers, message, channel, files, expiration_date) {
  if (message) {
    message = {
      payload: {
        channel: channel || 'webchat',
        ...(expiration_date && { expiration_date }),
        ...composeMessage({ content: message, files }),
      },
    };
  } else {
    message = undefined;
  }
  return {
    payload: {
      ...(customers !== undefined && { customers }),
      message,
    },
  };
}

function getCustomerRequestOptions({ rootGetters }, params) {
  params.date_type = params.date_type || 'joined_date';
  set(params, 'views', rootGetters['agent/customersDashboardViews']);

  return {
    params,
    paramsSerializer: serializedParams => qs.stringify(serializedParams, {
      arrayFormat: 'brackets',
      encode: true,
      skipNulls: true,
    }),
  };
}

export default {
  getCustomers({ commit, rootGetters }, { params, update }) {
    return axios.get(getUrl('dashboard', 'customers'), getCustomerRequestOptions({ rootGetters }, params))
      .then((data) => {
        const customers = data.data.models;
        if (update) {
          commit('CLEAR_STATE');
        }
        commit('SET_CUSTOMERS', customers);
        commit('SET_TOTAL_CUSTOMERS', get(data, 'data.pagination.rowCount', 0));
        return { customers, pagination: data.data.pagination };
      })
      .catch((err) => {
        if (update) {
          commit('CLEAR_STATE');
        }
        throw err;
      });
  },

  async exportCustomers(context, params) {
    return axios.get(getUrl('dashboard', 'customers/export'), {
      ...getCustomerRequestOptions(context, omit(params, ['limit', 'offset'])),
      responseType: 'blob',
    });
  },

  clearCustomers({ commit }) {
    commit('CLEAR_STATE');
  },

  selectCustomer({ commit }, customer) {
    commit('SELECT_CUSTOMER', customer);
  },

  deselectCustomer({ commit }, customer) {
    commit('DESELECT_CUSTOMER', customer);
  },

  selectCustomers({ commit }, customers) {
    commit('DESELECT_ALL_CUSTOMERS');
    for (const customer of customers) {
      commit('SELECT_CUSTOMER', customer);
    }
  },

  deselectAllCustomers({ commit }) {
    commit('DESELECT_ALL_CUSTOMERS');
  },

  removePrimaryAgentFromSelectedCustomers({ commit }, { customers, message }) {
    return axios.delete(getUrl('dashboard', 'customers/primary_agent'), {
      data: getCustomersMessagePayload(customers, message),
    })
      .then((data) => {
        const customersResponse = data.data.customers;
        commit('UPDATE_PRIMARY_AGENT', customersResponse);
        return customersResponse;
      });
  },

  async updatePrimaryAgentFromSelectedCustomers(context, { customers, message, agent, mode, files, selection }) {
    const requestPayload = getCustomersMessagePayload(undefined, message, null, files);
    Object.assign(requestPayload.payload, buildCustomerSelection(context, customers, selection));
    if (mode !== 'remove' || !bulkActionsEnabled(context)) {
      requestPayload.payload.agent = bulkActionsEnabled(context) && agent ? { id: Number(agent.id) } : agent;
    }
    requestPayload.payload.mode = mode;

    const res = await axios.put(getUrl('dashboard', 'customers/primary_agent'), requestPayload);
    return res.data;
  },

  async updateCustomerSegments(context, { mode, customers, segments, selection }) {
    const res = await axios.post(getUrl('bulk', 'customers/segments'), {
      mode,
      ...buildCustomerSelection(context, customers, selection),
      segments: bulkActionsEnabled(context) ? segments.map(String) : segments,
    });
    return res.data;
  },

  async updateCustomerAssigedAgents(context, { mode, customers, agents, selection }) {
    const res = await axios.post(getUrl('bulk', 'customers/agents'), {
      mode,
      ...buildCustomerSelection(context, customers, selection),
      agents: bulkActionsEnabled(context) ? agents.map(Number) : agents,
    });
    return res.data;
  },

  async sendMessageToSelectedCustomers(context,
    { customers, message, channel, files, expirationMessageDate, selection }) {
    const requestPayload = getCustomersMessagePayload(undefined, message, channel, files, expirationMessageDate);
    Object.assign(requestPayload.payload, buildCustomerSelection(context, customers, selection));
    const res = await axios.post(
      getUrl('dashboard', 'customers/message'),
      requestPayload,
    );
    return res.data;
  },

  async sendSurveyToSelectedCustomers(context, { customers, message, surveyType, selection }) {
    const requestPayload = getCustomersMessagePayload(undefined, message);
    Object.assign(requestPayload.payload, buildCustomerSelection(context, customers, selection));
    requestPayload.payload.survey_type = surveyType.toLowerCase();

    const res = await axios.post(getUrl('dashboard', 'customers/survey'), requestPayload);
    return res.data;
  },

  async closeConversationToSelectedCustomers(context, { customers, selection }) {
    const requestPayload = { payload: buildCustomerSelection(context, customers, selection) };

    const res = await axios.post(getUrl('dashboard', 'customers/close_conversation'), requestPayload);
    return res.data;
  },
};
